"use client";

import { useEffect, useState } from "react";
import { ChevronDown, DoorOpen, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Image from "@/components/Image";
import { ProcesionPublicCard, usePublicProcesion } from "@/components/cecop/ProcesionPublicCard";
import { CecopDiaPublicCard, useCecopDiaPublico } from "@/components/cecop/CecopDiaPublicCard";

const MotionLink = motion(Link);

// Fondos que se van fundiendo entre sí en bucle.
export const BACKGROUNDS = [
  "/images/nazarenostriana.webp",
  "/images/virgen-velas.webp",
  "/images/hero/hero-1.webp",
  "/images/hero/hero-2.webp",
  "/images/hero/hero-3.webp",
  "/images/hero/hero-4.webp",
];
const BG_INTERVAL_MS = 9000;

const buttonShape = {
  clipPath:
    "polygon(0% 0%, calc(100% - 20px) 0%, 100% 20px, 100% 100%, 20px 100%, 0% calc(100% - 20px))",
};

const buttonMotion = {
  whileHover: { scale: 1.06, y: -4 },
  whileTap: { scale: 0.95 },
  transition: { type: "spring" as const, stiffness: 420, damping: 22 },
};

function ButtonShine() {
  return (
    <span
      className="pointer-events-none absolute inset-0 -translate-x-[120%] bg-gradient-to-r from-transparent via-white/35 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[120%]"
      style={{ transform: "skewX(-20deg)" }}
    />
  );
}

function EntrarButton() {
  return (
    <MotionLink
      to="/apply"
      style={buttonShape}
      {...buttonMotion}
      className="group relative inline-flex h-14 items-center justify-center gap-2 overflow-hidden bg-gradient-to-r from-primary to-amber-500 px-9 text-base font-bold text-primary-foreground shadow-[0_0_25px_rgba(250,204,21,0.45)]"
    >
      <ButtonShine />
      <span className="relative z-10 flex items-center gap-2">
        <motion.span
          animate={{ x: [0, -3, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          className="flex"
        >
          <DoorOpen className="h-5 w-5" />
        </motion.span>
        Entrar al servidor
      </span>
    </MotionLink>
  );
}

function DiscordButton() {
  return (
    <motion.a
      href="https://discord.gg/rmJGq5YbTE"
      target="_blank"
      rel="noopener noreferrer"
      style={buttonShape}
      {...buttonMotion}
      className="group relative inline-flex h-14 items-center justify-center gap-2 overflow-hidden border border-white/25 bg-white/5 px-9 text-base font-bold text-white backdrop-blur-md transition-colors hover:bg-white/10"
    >
      <ButtonShine />
      <span className="relative z-10 flex items-center gap-2">
        <motion.span
          animate={{ rotate: [0, -8, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="flex"
        >
          <MessageCircle className="h-5 w-5" />
        </motion.span>
        Únete a Discord
      </span>
    </motion.a>
  );
}

/** Cartel de fin de edición. Sustituye al emblema "11 JARTILAND". */
function FinalizadoCartel() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/40 bg-black/55 px-8 py-5 shadow-[0_0_40px_rgba(250,204,21,0.25)] backdrop-blur-md md:px-12">
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-600" />
      <p className="font-headline text-2xl font-extrabold uppercase tracking-wide text-primary md:text-4xl">
        Jartiland 11 ha finalizado
      </p>
    </div>
  );
}

/** Invitación a bajar a la sección "Así fue la Semana Santa 2026". */
function VerSemanaSanta() {
  return (
    <a
      href="#semana-santa-2026"
      className="flex flex-col items-center gap-2 text-sm text-white/70 transition-colors hover:text-primary"
    >
      <span>¿Quieres ver cómo fue la Semana Santa 2026 de Jartiland?</span>
      <motion.span
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        className="flex"
      >
        <ChevronDown className="h-7 w-7 text-primary" />
      </motion.span>
    </a>
  );
}

/** Fondo con fundido encadenado entre las imágenes de `BACKGROUNDS`. */
function HeroBackground() {
  // La primera imagen se sortea en cada visita: nunca se entra a la misma foto.
  // El sorteo va en el inicializador perezoso de `useState` (una sola vez, no en
  // cada render) para que no cambie con cada repintado.
  const [index, setIndex] = useState(() => Math.floor(Math.random() * BACKGROUNDS.length));

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % BACKGROUNDS.length), BG_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="absolute inset-0">
      {BACKGROUNDS.map((src, i) => (
        <Image
          key={src}
          src={src}
          alt="Procesión en el servidor de Minecraft Jartiland"
          // Todas montadas y superpuestas: solo cambia la opacidad, así el
          // cambio es un fundido real y no un salto al cargar la imagen.
          className="absolute inset-0 h-full w-full scale-105 object-cover"
          style={{
            opacity: i === index ? 1 : 0,
            transition: 'opacity 2500ms ease-in-out',
          }}
          data-ai-hint="minecraft cofrade procession"
        />
      ))}
      {/* Velo de profundidad, ligero para que la imagen se vea bien */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/35 to-background/5" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-transparent to-background/50" />
    </div>
  );
}

export function Hero() {
  // Si hay tarjeta pública activa, sustituye al emblema y a los botones.
  const { procesion, live } = usePublicProcesion();
  // Un DÍA de Semana Santa activado en el CECOP manda sobre la procesión suelta:
  // es el panel del día entero, con todas sus cofradías.
  const dia = useCecopDiaPublico();
  const hasDia = !!dia.cecop;
  const hasCard = hasDia || !!procesion;

  return (
    <section className="relative flex min-h-screen w-full flex-col overflow-hidden py-20">
      <HeroBackground />

      {/* Glow ambiental dorado */}
      <div className="absolute -top-32 left-1/2 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
      <div className="absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-primary/10 blur-[100px]" />

      <div className="relative z-10 flex w-full flex-1 flex-col items-center justify-center gap-12 px-4 pt-12 text-center text-white md:gap-16 md:pt-16">
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="font-headline uppercase text-6xl font-extrabold tracking-tight md:text-9xl">
            <span className="bg-gradient-to-b from-primary via-primary to-amber-600 bg-clip-text text-transparent drop-shadow-[0_4px_20px_rgba(250,204,21,0.35)]">
              JARTILAND
            </span>
          </h1>

          <p className="mt-5 text-base font-semibold uppercase tracking-wide text-white/90 md:text-xl">
            El primer servidor de Minecraft cofrade
          </p>
          <p className="mt-1 text-base font-semibold uppercase tracking-wide text-primary md:text-xl">
            El Mod Cofrade original
          </p>

          <p className="mt-3 max-w-xl text-sm text-white/60 md:text-base">
            Servidor Cofrade de la comunidad de DarioelJartible
          </p>
        </motion.div>

        {hasDia ? (
          <CecopDiaPublicCard cecop={dia.cecop!} live={dia.live} dia={dia.dia} hermandades={dia.hermandades} />
        ) : hasCard ? (
          // Con tarjeta activa se ocultan emblema y botones: la tarjeta ya
          // lleva sus propios accesos (servidor, directo y Discord).
          <ProcesionPublicCard procesion={procesion!} live={live} />
        ) : (
          <motion.div
            className="flex flex-col items-center gap-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
          >
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
              <EntrarButton />
              <DiscordButton />
            </div>

            <FinalizadoCartel />
          </motion.div>
        )}
        {/* La cuenta atrás a Ramos y las próximas procesiones se quitaron al
            acabar Jartiland 11 (siguen en el repo, sin montar). */}
      </div>

      <div className="relative z-10 mt-12 flex justify-center px-4 text-center">
        <VerSemanaSanta />
      </div>
    </section>
  );
}
